import { AuthService } from './authService';
import { CallTranscriptPart, Company, Project } from '../types';

type TranscriptRole = 'model' | 'user';

export class OpenAiRealtimeService {
  private peerConnection: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private mediaStream: MediaStream | null = null;
  private remoteAudioEl: HTMLAudioElement | null = null;
  private monitorContext: AudioContext | null = null;
  private inputAnalyser: AnalyserNode | null = null;
  private outputAnalyser: AnalyserNode | null = null;
  private inputLevel = 0;
  private outputLevel = 0;
  private monitorFrame: number | null = null;
  private transcript: Array<{ role: TranscriptRole; text: string; timestamp: number }> = [];
  private activeModelTranscript = '';
  private isConnected = false;
  private userMuted = false;
  private temporarilySuspendedMic = false;
  private lastError: string | null = null;

  private applyMicTrackState(enabled: boolean) {
    this.mediaStream?.getAudioTracks().forEach((track) => {
      track.enabled = enabled;
    });
  }

  private pushTranscript(role: TranscriptRole, text: string) {
    const cleaned = text.trim();
    if (!cleaned) return;

    const now = Date.now();
    const previous = this.transcript[this.transcript.length - 1];
    if (previous && previous.role === role && now - previous.timestamp < 1500) {
      previous.text = `${previous.text} ${cleaned}`.trim();
      previous.timestamp = now;
      return;
    }

    this.transcript.push({ role, text: cleaned, timestamp: now });
  }

  getTranscript() {
    return this.transcript
      .map((entry) => `${entry.role === 'model' ? 'BDAT' : 'Contactpersoon'}: ${entry.text}`)
      .join('\n');
  }

  getTranscriptParts(): CallTranscriptPart[] {
    return this.transcript.map((entry) => ({
      speaker: entry.role === 'model' ? 'agent' : 'contact',
      text: entry.text,
    }));
  }

  setMuted(muted: boolean) {
    this.userMuted = muted;
    if (!this.temporarilySuspendedMic) {
      this.applyMicTrackState(!muted);
    }
  }

  getAudioLevels() {
    return {
      input: this.inputLevel,
      output: this.outputLevel,
    };
  }

  getLastError() {
    return this.lastError;
  }

  private computeAnalyserLevel(analyser: AnalyserNode | null) {
    if (!analyser) return 0;
    const buffer = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(buffer);
    let sum = 0;
    for (let index = 0; index < buffer.length; index += 1) {
      const value = (buffer[index] - 128) / 128;
      sum += value * value;
    }
    return Math.min(1, Math.sqrt(sum / buffer.length) * 3.4);
  }

  private startMonitorLoop() {
    if (this.monitorFrame !== null) {
      cancelAnimationFrame(this.monitorFrame);
    }

    const update = () => {
      this.inputLevel = this.computeAnalyserLevel(this.inputAnalyser);
      this.outputLevel = this.computeAnalyserLevel(this.outputAnalyser);
      this.monitorFrame = requestAnimationFrame(update);
    };

    update();
  }

  private stopMonitorLoop() {
    if (this.monitorFrame !== null) {
      cancelAnimationFrame(this.monitorFrame);
      this.monitorFrame = null;
    }
    this.inputLevel = 0;
    this.outputLevel = 0;
  }

  private attachRemoteAudio() {
    if (!this.remoteAudioEl) {
      const audio = document.createElement('audio');
      audio.autoplay = true;
      audio.setAttribute('playsinline', 'true');
      audio.style.position = 'fixed';
      audio.style.width = '1px';
      audio.style.height = '1px';
      audio.style.opacity = '0';
      audio.style.pointerEvents = 'none';
      audio.style.left = '-9999px';
      audio.volume = 1;
      document.body.appendChild(audio);
      this.remoteAudioEl = audio;
    }
    return this.remoteAudioEl;
  }

  private handleRealtimeEvent(event: any) {
    if (!event?.type) return;

    if (event.type === 'session.updated' || event.type === 'conversation.item.created') {
      console.log('[OpenAI Realtime] Event:', event.type, event);
    }

    if (event.type === 'response.created') {
      console.log('[OpenAI Realtime] Event: response.created', {
        id: event.response?.id,
        status: event.response?.status,
      });
    }

    if (event.type === 'response.done') {
      const statusDetailsText = event.response?.status_details ? JSON.stringify(event.response.status_details) : null;
      console.log('[OpenAI Realtime] Event: response.done', {
        id: event.response?.id,
        status: event.response?.status,
        status_details: statusDetailsText,
        output_count: Array.isArray(event.response?.output) ? event.response.output.length : 0,
      });

      if (event.response?.status === 'failed') {
        if (statusDetailsText?.includes('insufficient_quota')) {
          this.lastError = 'OpenAI realtime heeft geen API-tegoed of quota meer. Voeg billing/credits toe in OpenAI Platform.';
        } else if (statusDetailsText) {
          this.lastError = `OpenAI realtime fout: ${statusDetailsText}`;
        } else {
          this.lastError = 'OpenAI realtime kon geen antwoord genereren.';
        }
      } else if (event.response?.status === 'completed') {
        this.lastError = null;
      }

      if (this.temporarilySuspendedMic) {
        this.temporarilySuspendedMic = false;
        window.setTimeout(() => {
          this.applyMicTrackState(!this.userMuted);
        }, 120);
      }
    }

    if (event.type === 'input_audio_buffer.speech_started' || event.type === 'input_audio_buffer.speech_stopped') {
      console.log('[OpenAI Realtime] Speech detectie:', event.type);
    }

    if (event.type === 'conversation.item.input_audio_transcription.completed' && event.transcript) {
      this.pushTranscript('user', event.transcript);
      return;
    }

    if ((event.type === 'response.audio_transcript.delta' || event.type === 'response.output_audio_transcript.delta') && event.delta) {
      this.activeModelTranscript += event.delta;
      return;
    }

    if (event.type === 'response.audio_transcript.done' || event.type === 'response.output_audio_transcript.done') {
      const text = event.transcript || this.activeModelTranscript;
      this.activeModelTranscript = '';
      this.pushTranscript('model', text);
      return;
    }

    if (event.type === 'response.output_text.delta' && event.delta) {
      this.activeModelTranscript += event.delta;
      return;
    }

    if (event.type === 'response.output_text.done') {
      const text = event.text || event.output_text || this.activeModelTranscript;
      this.activeModelTranscript = '';
      this.pushTranscript('model', text);
      return;
    }

    if (event.type === 'error') {
      this.lastError = `OpenAI realtime eventfout: ${JSON.stringify(event)}`;
      console.error('[OpenAI Realtime] Event error:', JSON.stringify(event));
    }
  }

  async connect(
    project: Project | null,
    company: Company | null,
    onStatusChange: (status: string) => void,
    options: {
      apiKey: string;
      model: string;
      voice: string;
      transcriptionModel: string;
      instructions: string;
      audioDeviceId?: string;
    }
  ) {
    this.disconnect();
    onStatusChange('connecting');

    const audioConstraints: MediaTrackConstraints = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
    };

    if (options.audioDeviceId?.trim()) {
      audioConstraints.deviceId = { exact: options.audioDeviceId.trim() };
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
    } catch (error: any) {
      if (error?.name === 'NotAllowedError') {
        throw new Error('Microfoontoegang geweigerd in de browser.');
      }
      if (error?.name === 'NotFoundError' || error?.name === 'OverconstrainedError') {
        throw new Error('De gekozen microfoon is niet beschikbaar. Kies in Stem een geldige invoerbron.');
      }
      throw error;
    }

    this.mediaStream = stream;
    this.lastError = null;
    this.userMuted = false;
    this.temporarilySuspendedMic = false;
    this.transcript = [];
    this.activeModelTranscript = '';
    this.monitorContext = new AudioContext();
    if (this.monitorContext.state === 'suspended') {
      await this.monitorContext.resume();
    }
    const inputSource = this.monitorContext.createMediaStreamSource(stream);
    this.inputAnalyser = this.monitorContext.createAnalyser();
    this.inputAnalyser.fftSize = 512;
    inputSource.connect(this.inputAnalyser);
    this.startMonitorLoop();

    const pc = new RTCPeerConnection();
    this.peerConnection = pc;

    stream.getTracks().forEach((track) => pc.addTrack(track, stream));

    pc.ontrack = (event) => {
      const audioEl = this.attachRemoteAudio();
      audioEl.srcObject = event.streams[0];
      void audioEl.play().catch((error) => {
        console.warn('[OpenAI Realtime] Remote audio play mislukt:', error);
      });

      if (this.monitorContext) {
        const outputSource = this.monitorContext.createMediaStreamSource(event.streams[0]);
        this.outputAnalyser = this.monitorContext.createAnalyser();
        this.outputAnalyser.fftSize = 512;
        outputSource.connect(this.outputAnalyser);
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        this.isConnected = true;
        onStatusChange('active');
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected' || pc.connectionState === 'closed') {
        this.isConnected = false;
        onStatusChange(pc.connectionState === 'failed' ? 'error' : 'disconnected');
      }
    };

    const channel = pc.createDataChannel('oai-events');
    this.dataChannel = channel;
    channel.onmessage = (message) => {
      try {
        this.handleRealtimeEvent(JSON.parse(message.data));
      } catch (error) {
        console.warn('[OpenAI Realtime] Ongeldig event:', error);
      }
    };

    channel.onopen = () => {
      const projectName = project?.name || 'BDAT';
      const companyName = company?.name || 'de lead';
      const openingSeed = `Open het gesprek nu. Stel jezelf in natuurlijk Nederlands voor namens ${projectName} en richt je op ${companyName}. Vraag eerst of het gelegen komt. Houd het bij maximaal twee korte zinnen en lees geen projectomschrijving of briefing op.`;
      this.temporarilySuspendedMic = true;
      this.applyMicTrackState(false);

      channel.send(JSON.stringify({
        type: 'session.update',
        session: {
          type: 'realtime',
          model: options.model,
          instructions: options.instructions,
          output_modalities: ['audio'],
          audio: {
            input: {
              format: {
                type: 'audio/pcm',
                rate: 24000,
              },
              noise_reduction: { type: 'near_field' },
              transcription: {
                model: options.transcriptionModel,
                language: 'nl',
              },
              turn_detection: {
                type: 'semantic_vad',
                eagerness: 'medium',
                create_response: true,
                interrupt_response: true,
              },
            },
            output: {
              format: {
                type: 'audio/pcm',
                rate: 24000,
              },
              voice: options.voice,
            },
          },
        },
      }));
      window.setTimeout(() => {
        if (channel.readyState !== 'open') return;

        channel.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [
              {
                type: 'input_text',
                text: openingSeed,
              },
            ],
          },
        }));

        channel.send(JSON.stringify({
          type: 'response.create',
          response: {
            output_modalities: ['audio'],
          },
        }));
      }, 160);
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const response = await AuthService.authFetch('/voice/openai/realtime/sdp', {
      method: 'POST',
      headers: Object.fromEntries(AuthService.authHeaders(true).entries()),
      body: JSON.stringify({
        sdp: offer.sdp,
        instructions: options.instructions,
        model: options.model,
        voice: options.voice,
        transcriptionModel: options.transcriptionModel,
        apiKey: options.apiKey,
      }),
    });

    if (!response.ok) {
      const rawPayload = await response.text();
      let parsedPayload: any = null;
      try {
        parsedPayload = JSON.parse(rawPayload);
      } catch {
        parsedPayload = null;
      }
      const message = parsedPayload?.hint
        ? `${parsedPayload.error} ${parsedPayload.hint}`
        : parsedPayload?.error || rawPayload || `OpenAI realtime SDP mislukt (${response.status})`;
      this.lastError = message;
      throw new Error(message);
    }

    const answerSdp = await response.text();
    await pc.setRemoteDescription({
      type: 'answer',
      sdp: answerSdp,
    });
  }

  disconnect() {
    this.stopMonitorLoop();
    this.dataChannel?.close();
    this.dataChannel = null;
    this.peerConnection?.close();
    this.peerConnection = null;
    this.mediaStream?.getTracks().forEach((track) => track.stop());
    this.mediaStream = null;
    if (this.remoteAudioEl) {
      this.remoteAudioEl.srcObject = null;
      this.remoteAudioEl.remove();
      this.remoteAudioEl = null;
    }
    this.inputAnalyser?.disconnect();
    this.outputAnalyser?.disconnect();
    this.inputAnalyser = null;
    this.outputAnalyser = null;
    void this.monitorContext?.close();
    this.monitorContext = null;
    this.isConnected = false;
    this.activeModelTranscript = '';
    this.userMuted = false;
    this.temporarilySuspendedMic = false;
    this.lastError = null;
  }
}
